import { Blockquote, Button } from '@ui';
import { useClubSlug } from 'components/Clubs/hooks';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PaymentMethodLogo from './PaymentMethodLogo';
import usePaymentMethodLabel from './usePaymentMethodLabel';
import { PaymentMethod, useClubPaymentMethods } from './usePaymentMethods';

interface PaymentMethodOfferProps {
    /** form field holding the selected subset, defaults to `payment_methods` */
    name?: string;
    /** field that toggles online payments; the offer only applies when it is on */
    dependsOn?: string;
    className?: string;
}

/**
 * Shows which payment methods the club actually has enabled in its Mollie dashboard
 * and lets an admin restrict which of them bookers get to choose from.
 *
 * Selecting nothing means "offer all of them" — that is the pre-existing behaviour,
 * so leaving this untouched changes nothing for existing spaces.
 */
export default function PaymentMethodOffer({
    name = 'payment_methods',
    dependsOn = 'online_payments',
    className
}: PaymentMethodOfferProps) {
    const { t } = useTranslation('general');
    const { label } = usePaymentMethodLabel();
    const clubSlug = useClubSlug();
    const { watch, setValue } = useFormContext();

    const onlinePayments = watch(dependsOn);
    const selected: string[] = watch(name) ?? [];

    // only ask Mollie once online payments are actually on — otherwise every space
    // form open would cost a request, and a cold cache a full Mollie round-trip
    const { data, isLoading } = useClubPaymentMethods(
        onlinePayments ? clubSlug : undefined,
        undefined,
        'space_manager'
    );
    const methods: PaymentMethod[] = data?.data ?? [];

    if (!onlinePayments || isLoading) return null;

    if (methods.length === 0) {
        return (
            <Blockquote color="warning" className={className}>
                {t('mollie.paymentMethods.emptyState')}
            </Blockquote>
        );
    }

    const toggle = (id: string) => {
        setValue(name, selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id], {
            shouldDirty: true
        });
    };

    return (
        <div className={className}>
            <span className="tw-block tw-text-sm tw-font-medium tw-text-accents-8">
                {t('mollie.paymentMethods.offerTitle')}
            </span>
            <span className="tw-mb-2 tw-block tw-text-xs tw-text-accents-6">
                {t('mollie.paymentMethods.offerHint')}
            </span>
            <div className="tw-flex tw-flex-wrap tw-gap-2">
                {methods.map((method) => {
                    const isOffered = selected.includes(method.id);

                    return (
                        <Button
                            key={method.id}
                            type="button"
                            size="md"
                            flat
                            color={isOffered ? 'success' : 'light'}
                            icon={<PaymentMethodLogo method={method} />}
                            aria-pressed={isOffered}
                            className="tw-min-h-[44px]"
                            onPress={() => toggle(method.id)}
                        >
                            {label(method)}
                        </Button>
                    );
                })}
            </div>
            {selected.length === 0 && (
                <span className="tw-mt-2 tw-block tw-text-xs tw-text-accents-6">
                    {t('mollie.paymentMethods.offerAllHint')}
                </span>
            )}
        </div>
    );
}
